import { numFont } from '../../../utils/fonts';
import React from 'react';
import { Box, Typography } from '@mui/material';

import { useWidgetFrame } from '../frameContext';
import { labelSx, valueFontSize, valueSx } from './mobileText';

/**
 * Breakdown tile (design template 2): a headline number with coloured count chips, and at M
 * an extra key/value row list. Headless at S/W (draws its own title line), framed at M.
 */
export default function BreakdownTile({ icon: Icon, title, value, unit, chips = [], rows = [], onClick }) {
  const frame = useWidgetFrame();
  const headless = frame?.headless;
  const edit = frame?.mode === 'edit';
  const compact = frame?.size === 'S';
  const mobile = Boolean(frame?.isMobile);
  const clickable = Boolean(onClick) && !edit && !frame?.inPreview;
  // A 140 px tile has room for one chip row (two at most). Zero-count chips are noise there,
  // so they are dropped whenever the row would not fit; the headline number keeps the total.
  const shownChips = (() => {
    if (!headless || chips.length <= 2) return chips;
    const nonZero = chips.filter((chip) => Number(chip.n) !== 0);
    return (nonZero.length ? nonZero : chips.slice(0, 2)).slice(0, compact ? 3 : 4);
  })();

  return (
    <Box
      onClick={clickable ? onClick : undefined}
      sx={{ flex: 1, minHeight: 0, px: 2.25, py: headless ? 1.75 : 2, display: 'flex', flexDirection: 'column', gap: 0.75, cursor: clickable ? 'pointer' : 'default', overflow: 'hidden' }}
    >
      {headless && !edit ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', ...labelSx(mobile) }}>
            {title || frame?.title}
          </Typography>
          {Icon ? <Icon sx={{ fontSize: 18, color: '#94a3b8', flexShrink: 0 }} /> : null}
        </Box>
      ) : null}
      {/* On mobile the unit wraps under the number instead of squeezing it. */}
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, minWidth: 0, flexWrap: mobile ? 'wrap' : 'nowrap' }}>
        <Typography
          data-widget-value=""
          sx={{ fontSize: valueFontSize(value, mobile, 26), lineHeight: 1.1, fontWeight: 700, letterSpacing: '-0.5px', fontFamily: "'Plus Jakarta Sans', sans-serif", ...valueSx(mobile) }}
        >
          {value}
        </Typography>
        {unit ? (
          <Typography sx={{ fontSize: 12, color: 'text.secondary', ...labelSx(mobile) }}>
            {unit}
          </Typography>
        ) : null}
      </Box>
      {shownChips.length ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, maxHeight: mobile ? 'none' : 46, overflow: 'hidden' }}>
          {shownChips.map((chip, index) => (
            <Box
              key={index}
              component={chip.onClick ? 'button' : 'span'}
              type={chip.onClick ? 'button' : undefined}
              onClick={
                chip.onClick && !edit
                  ? (event) => {
                      event.stopPropagation();
                      chip.onClick();
                    }
                  : undefined
              }
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 0.9,
                height: 20,
                borderRadius: '10px',
                fontSize: 11,
                lineHeight: 1,
                fontWeight: 600,
                fontFamily: 'inherit',
                border: 0,
                cursor: chip.onClick && !edit ? 'pointer' : 'default',
                bgcolor: chip.bg || '#f8fafc',
                color: chip.color || '#64748b',
                whiteSpace: 'nowrap',
              }}
            >
              {chip.color ? <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: chip.color }} /> : null}
              <strong>{chip.n}</strong> {chip.label}
            </Box>
          ))}
        </Box>
      ) : null}
      {!headless && rows.length ? (
        <Box sx={{ mt: 'auto', borderTop: '1px solid #f1f5f9', pt: 0.5 }}>
          {rows.map((row, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, height: 36, fontSize: 13 }}>
              {row.icon ? <row.icon sx={{ fontSize: 18, color: '#94a3b8' }} /> : null}
              <Typography noWrap sx={{ flex: 1, fontSize: 13, color: 'text.secondary' }}>
                {row.label}
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600, ...numFont }}>{row.value}</Typography>
            </Box>
          ))}
        </Box>
      ) : null}
    </Box>
  );
}
